/*
  Quick view drawer.

  Opens from the icon buttons on a product card and shows enough of the product
  to choose a variant and add it to the cart, without leaving the page.

  The contents are not built here. They are fetched from the product URL with
  section_id=quick-view added to whatever query string it already has, and
  injected as-is, so the drawer shows
  server-rendered Liquid with real prices and real inventory state, and Dawn's
  own <product-info>, <variant-selects> and <product-form> take over from
  there. This file only owns the shell: opening, closing, focus and the fetch.

  It deliberately does not extend Dawn's ModalDialog. That class is built for a
  centred modal that is already in the document, and its show/hide assume the
  content is static. A right-hand drawer with fetched contents is a different
  shape, and the focus and scroll-lock helpers it would have given us
  (trapFocus, removeTrapFocus) are free functions in global.js that this uses
  directly anyway.
*/
class QuickViewDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-quick-view-panel]');
    this.content = this.querySelector('[data-quick-view-content]');
    this.loading = this.querySelector('[data-quick-view-loading]');
    this.status = this.querySelector('[data-quick-view-status]');
    this.openedBy = null;
    this.currentUrl = null;

    this.querySelector('[data-quick-view-close]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-quick-view-overlay]')?.addEventListener('click', () => this.close());

    /*
      Delegated at the document, not bound per button. Cards are re-rendered
      constantly - collection filtering, cart drawer section refreshes, the
      theme editor - and any listener attached to a button at load time is gone
      after the first re-render. Delegation survives all of it and costs one
      listener.
    */
    this.onDocumentClick = (event) => {
      const trigger = event.target.closest('[data-quick-view-url]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger.getAttribute('data-quick-view-url'), trigger);
    };
    document.addEventListener('click', this.onDocumentClick);

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    };
    document.addEventListener('keydown', this.onKeydown);

    this.watchCartDrawer();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.cartDrawerObserver?.disconnect();
  }

  /* Handover to the cart drawer --------------------------------------- */

  /*
    Adding to cart from this drawer opens the cart drawer on top of it, and
    the two cannot both be open. Dawn's focus helpers are page-level
    singletons: trapFocus keeps its handlers in one shared object, so whoever
    traps last owns the only trap on the page, and the scroll lock is a single
    class on <body>. Left open underneath, this drawer would sit there with
    its `open` attribute, no trap of its own, and would have its scroll lock
    removed out from under it when the cart drawer closed.

    The cart drawer's class is observed rather than the cartUpdate event,
    because every route that opens it goes through that class - add to cart
    from a card, from this drawer, from the header cart link - and only some
    of them publish cartUpdate.

    Ordering is the point. CartDrawer.open() adds the class, then installs its
    focus trap on transitionend. A mutation callback runs at the microtask
    checkpoint after the class is set, which is comfortably before that
    transition ends, so this drawer has always stood down before the cart
    drawer takes the trap. Neither section touches the other's DOM.
  */
  watchCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    this.cartDrawerObserver = new MutationObserver(() => {
      if (!this.hasAttribute('open')) return;
      if (!cartDrawer.classList.contains('active')) return;
      this.handOverTo(cartDrawer);
    });

    this.cartDrawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }

  /*
    Standing down for the cart drawer is not the same as closing on Escape.

    The scroll lock stays: it is one class on <body> shared by both drawers,
    and releasing it here would unlock the page underneath a cart drawer that
    has just locked it. CartDrawer.close() removes it later, once, which is
    the correct owner.

    Focus is not returned to the card either. The cart drawer is about to trap,
    and pulling focus back to a button behind it would scroll the page there
    and then be overridden a frame later. The trigger is handed to the cart
    drawer instead, so closing the cart drawer lands the visitor back on the
    button they started from rather than on the body.
  */
  handOverTo(cartDrawer) {
    const trigger = this.openedBy;
    this.close({ releaseScrollLock: false, restoreFocus: false });
    if (trigger) cartDrawer.setActiveElement(trigger);
  }

  open(url, trigger) {
    this.openedBy = trigger;
    this.currentUrl = url;

    trigger.setAttribute('aria-disabled', 'true');
    trigger.classList.add('loading');
    trigger.querySelector('.loading__spinner')?.classList.remove('hidden');

    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    this.setLoading(true);

    /*
      Focus moves to the panel immediately rather than waiting for the fetch.
      A screen reader user who activated the button should be told something
      opened straight away, not after a network round trip, and the status
      region below announces the loading state while the request is in flight.
    */
    trapFocus(this.panel, this.panel);

    /*
      Built with the URL API rather than string concatenation, because product
      URLs do not always arrive clean. A card in the related products row
      carries Shopify's recommendation attribution -
      ?pr_prod_strat=...&pr_rec_id=...&pr_ref_pid=... - so appending a second
      "?" produced a URL with two of them. Shopify ignores everything after the
      first, section_id is never read as a parameter, and the response is the
      entire product page rather than the section. The drawer then finds no
      product in it and falls through to the error path, which navigates away.
      The symptom was a quick view that opened, spun, and then left the page.

      searchParams.set also replaces rather than appends, so a URL that somehow
      already carried a section_id cannot end up with two, and it leaves the
      existing parameters untouched - the recommendation attribution survives
      into the request, which is the point of Shopify setting it.
    */
    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('section_id', 'quick-view');

    fetch(requestUrl.toString())
      .then((response) => {
        if (!response.ok) throw new Error(`Quick view request failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        if (!html.querySelector('product-info')) throw new Error('Quick view response contained no product');

        /*
          Inject the whole section wrapper, not just the <product-info> inside
          it. The stylesheet links and the <script src> tags for
          product-info.js and product-form.js are siblings of that element, not
          children, so lifting the element alone leaves them behind: the drawer
          renders, and then nothing responds because the custom elements it is
          made of were never defined.

          setInnerHTML rather than innerHTML for the same reason. Scripts
          assigned through innerHTML do not execute; that helper re-creates
          them so they do.
        */
        const section = html.querySelector('.shopify-section') || html.body;
        HTMLUpdateUtility.setInnerHTML(this.content, section.innerHTML);

        /*
          Those scripts load over the network, and until they do, <product-form>
          is an unupgraded element wrapping a plain form. A visitor who hits add
          to cart inside that window gets a native POST and a full page load to
          /cart, which is not broken but is not the drawer either. So the
          spinner stays up until the elements are actually defined.

          Raced against a timeout because whenDefined never rejects. If a script
          fails to load the drawer still becomes usable, and the form degrades
          to that same native POST rather than hanging on a spinner forever.
        */
        return Promise.race([
          Promise.all([customElements.whenDefined('product-info'), customElements.whenDefined('product-form')]),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      })
      .then(() => {
        if (!this.hasAttribute('open')) return;
        this.setLoading(false);

        // Re-trap now that there is real content, and land focus on it.
        removeTrapFocus();
        trapFocus(this.panel, this.panel);
      })
      .catch((error) => {
        /*
          Never fail silently. If the drawer cannot be built, send the visitor
          to the page that definitely works. Closing first restores body scroll
          and focus state before the navigation commits.
        */
        console.error(error);
        this.close();
        window.location = url;
      })
      .finally(() => {
        trigger.removeAttribute('aria-disabled');
        trigger.classList.remove('loading');
        trigger.querySelector('.loading__spinner')?.classList.add('hidden');
      });
  }

  /*
    Both options are true for every ordinary close - the button, the overlay,
    Escape. They are only turned off by handOverTo above, where the cart
    drawer owns the scroll lock and the focus that follows.
  */
  close({ releaseScrollLock = true, restoreFocus = true } = {}) {
    if (!this.hasAttribute('open')) return;

    this.removeAttribute('open');
    if (releaseScrollLock) document.body.classList.remove('overflow-hidden');
    removeTrapFocus(restoreFocus ? this.openedBy : null);

    /*
      The contents are cleared on close rather than cached. A drawer holds a
      live add-to-cart form and a quantity bound to current inventory; showing
      a stale copy on the next open would be showing the visitor numbers that
      may no longer be true. The clear is deferred past the slide-out so the
      panel does not visibly empty itself while animating.
    */
    setTimeout(() => {
      if (this.hasAttribute('open')) return;
      this.content.innerHTML = '';
      this.setLoading(true);
    }, 400);

    this.openedBy = null;
    this.currentUrl = null;
  }

  setLoading(isLoading) {
    this.loading.classList.toggle('hidden', !isLoading);
    this.content.classList.toggle('hidden', isLoading);
    if (this.status) {
      // Announced rather than shown; the string is translated in Liquid.
      this.status.textContent = isLoading ? this.dataset.loadingText || '' : '';
    }
  }
}

customElements.define('quick-view-drawer', QuickViewDrawer);

/*
  Confirmed state on the card's add button.

  Dawn's ProductForm owns the request and clears its own loading class in a
  finally block, but it has no notion of "done". It does publish cartUpdate on
  success, carrying the variant id that was added, and that is enough to find
  the button that caused it: the card forms each hold their variant in a hidden
  [name=id] input.

  Matching on variant rather than on the click means the confirmation only
  appears when the cart actually changed. A rejected add publishes cartError
  instead and the button is left alone, which is what Dawn's sold-out fallback
  in the snippet expects.
*/
document.addEventListener('DOMContentLoaded', () => {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source, productVariantId }) => {
    if (source !== 'product-form' || !productVariantId) return;

    document.querySelectorAll('.card-icons__form input[name="id"]').forEach((input) => {
      if (input.value !== String(productVariantId)) return;
      const button = input.form?.querySelector('.card-icons__button--add');
      if (!button) return;

      button.classList.add('card-icons__button--added');
      clearTimeout(button.addedTimeout);
      button.addedTimeout = setTimeout(() => button.classList.remove('card-icons__button--added'), 2000);
    });
  });
});
